import {Grid, GridItem} from '@/components/ui/grid-atoms/GridAtoms'
import {TreatmentRequestExtended, TreatmentRequestScore} from '@/features/treatment-requests/types'
import {Trans, useTranslation} from '@/translations/i18n'
import {ObjectKeys} from '@/utilities/helpers'
import {DetailsInformationObservedField} from '@/components/commons/details-information-observed-field/DetailsInformationObservedField'
import {z} from 'zod'
import {useMemo} from 'react'
import {StyledTreatmentRequestScoreGridItemData, StyledTreatmentRequestTotalScore} from './style'
import {useTreatmentRequestScores} from '@/features/treatment-requests/hooks/useTreatmentRequestScores'
import {Text} from '@/components/ui/text/Text'
import {remapTreatmentRequestToDefaultValues} from '../../utils'

type TreatmentRequestScoreKey = keyof z.infer<typeof TreatmentRequestScore>

export const TreatmentRequestScoresDetails = ({treatmentRequest}: {treatmentRequest: TreatmentRequestExtended}) => {
    const {t} = useTranslation()
    const defaultValues = remapTreatmentRequestToDefaultValues(treatmentRequest)

    const {scoresMap} = useTreatmentRequestScores({
        serviceType: treatmentRequest.serviceType,
        diseases: defaultValues.diseases
    })

    const isIncompleteScore = useMemo(
        () => ObjectKeys(scoresMap).some(scoreKey => treatmentRequest.fieldsToCompile.includes(scoreKey)),
        [scoresMap, treatmentRequest.fieldsToCompile]
    )

    return (
        <Grid columns={2} fullWidth columnGap={4}>
            {ObjectKeys(scoresMap).map(scoreKey => {
                const scoreData = scoresMap[scoreKey]
                if (!scoreData) return null

                return (
                    <StyledTreatmentRequestScoreGridItemData
                        justify="space-between"
                        key={scoreKey}
                        align={treatmentRequest.serviceType === 'adults' ? 'center' : 'center'}
                    >
                        <Text palette="neutral" shade="600" size="textSm">
                            {t(scoreData.label)}
                        </Text>

                        <DetailsInformationObservedField<TreatmentRequestScoreKey>
                            name="score"
                            hideIcons={['success']}
                            value={
                                treatmentRequest.scores.find(score => score.type == scoreKey) && (
                                    <Text palette="neutral" shade="950" size="textSm" weight="500">
                                        {treatmentRequest.scores.find(score => score.type == scoreKey)?.score}
                                    </Text>
                                )
                            }
                        />
                    </StyledTreatmentRequestScoreGridItemData>
                )
            })}

            <GridItem spanColumn={2}>
                <StyledTreatmentRequestTotalScore>
                    <Trans
                        i18nKey={'commons:total_score_x_bolded'}
                        values={{
                            count: isIncompleteScore
                                ? t('treatment_requests:incomplete_score')
                                : treatmentRequest.totalScore
                        }}
                        components={{b: <b />}}
                    />
                </StyledTreatmentRequestTotalScore>
            </GridItem>
        </Grid>
    )
}
